import { RefreshCw } from 'lucide-react'
import { useDashboardKPIs, useParcelProgress, useDisciplineProgress, useAlerts } from '@/hooks/useDashboard'
import KPICards from './KPICards'
import ProgressByParcelChart from './ProgressByParcel'
import ProgressByDisciplineChart from './ProgressByDiscipline'
import AlertsTable from './AlertsTable'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Demo timeline data
const DEMO_TIMELINE = [
  { date: '01/03', pct: 38.2 }, { date: '05/03', pct: 39.1 }, { date: '10/03', pct: 40.5 },
  { date: '15/03', pct: 42.0 }, { date: '20/03', pct: 43.8 }, { date: '25/03', pct: 45.2 },
  { date: '28/03', pct: 47.3 },
]

export default function Dashboard() {
  const kpisQuery       = useDashboardKPIs()
  const parcelQuery     = useParcelProgress()
  const disciplineQuery = useDisciplineProgress()
  const alertsQuery     = useAlerts()

  const refetchAll = () => {
    kpisQuery.refetch()
    parcelQuery.refetch()
    disciplineQuery.refetch()
    alertsQuery.refetch()
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Resumen ejecutivo</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <button onClick={refetchAll} className="btn-secondary text-sm gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* KPI Cards */}
      <KPICards kpis={kpisQuery.data ?? null} loading={kpisQuery.isLoading} />

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ProgressByParcelChart
          data={(parcelQuery.data ?? []) as { parcel_id: string; parcel_name: string; parcel_number: number; progress_pct: number }[]}
          loading={parcelQuery.isLoading}
        />
        <ProgressByDisciplineChart
          data={disciplineQuery.data ?? []}
          loading={disciplineQuery.isLoading}
        />
      </div>

      {/* Timeline + Alerts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Tendencia */}
        <div className="card p-5">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Tendencia de avance</h3>
            <p className="text-xs text-gray-400 mt-0.5">Últimos 30 días</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={DEMO_TIMELINE} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis domain={[35, 55]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => [`${v}%`, 'Avance']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E7EB' }} />
              <Line type="monotone" dataKey="pct" stroke="#2563EB" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: '#2563EB' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Alerts */}
        <div className="xl:col-span-2">
          <AlertsTable alerts={(alertsQuery.data ?? []) as any} loading={alertsQuery.isLoading} maxRows={8} />
        </div>
      </div>

    </div>
  )
}
